import {
  Autocomplete,
  Box,
  Button,
  Grid,
  InputAdornment,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import {
  getAnnualSalesTaxReport,
  approveAnnualTax,
  sendAnnualTaxReminder
} from 'src/services/clientService';
import { formatClientId } from 'src/utils/helperFunctions';
import moment from 'moment';
import { BarLoader } from 'react-spinners';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { NotificationManager } from 'react-notifications';
import { states } from 'src/constants/clients';

interface Column {
  id:
    | 'sno'
    | 'clientID'
    | 'state'
    | 'legalBusinessName'
    | 'doingBusinessAs'
    | 'contactPerson'
    | 'contactEmail'
    | 'contactNumber'
    | 'status'
    | 'actions';
  label: string;
  minWidth?: number;
  align?: 'right';
}

const columns: Column[] = [
  { id: 'sno', label: 'S.No' },
  { id: 'clientID', label: 'Client ID' },
  { id: 'state', label: 'State' },
  { id: 'legalBusinessName', label: 'Legal Business Name', minWidth: 250 },
  { id: 'doingBusinessAs', label: 'Doing Business As', minWidth: 250 },
  { id: 'contactPerson', label: 'Contact person', minWidth: 150 },
  { id: 'contactEmail', label: 'Contact Email' },
  { id: 'contactNumber', label: 'Contact Number' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions' }
];

const AnnualReport = () => {
  const [selectedDate, setSelectedDate] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [unSortedClients, setUnSortedClients] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedState, setSelectedState] = useState('All');
  const [searchValue, setSearchValue] = useState('');

  const styles = {
    cell: {
      border: '1px solid #E3E1D9',
      borderCollapse: 'collapse',
      padding: '6px'
    }
  };

  const sortedSalesTax = () => {
    if (orderBy === '') {
      return clients;
    }
    return clients.slice().sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return (order === 'asc' ? 1 : -1) * aValue.localeCompare(bValue);
      }
      return (order === 'asc' ? 1 : -1) * (aValue - bValue);
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getClients = async (date: Date) => {
    try {
      setLoading(true);
      const year = +moment(date).format('YYYY');
      const data = await getAnnualSalesTaxReport(year);
      const clientRows = data.data;
      setClients(clientRows);
      setUnSortedClients(clientRows);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleYearChange = (date) => {
    setSelectedDate(date);
    getClients(date);
  };

  const handleExportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    worksheet.columns = [
      { header: 'S.No', key: 'sno', width: 6 },
      { header: 'Client Id', key: 'id', width: 6 },
      { header: 'State', key: 'state', width: 16 },
      { header: 'Legal Business Name', key: 'legalBusinessName', width: 30 },
      { header: 'Doing Business As', key: 'doingBusinessAs', width: 30 },
      { header: 'Contact person', key: 'contactPerson', width: 18 },
      { header: 'Contact Email', key: 'contactEmail', width: 18 },
      { header: 'Contact Number', key: 'contactNumber', width: 18 },
      { header: 'Status', key: 'status', width: 10 }
    ];

    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFF00' }
    };

    sortedSalesTax().forEach((row, index) => {
      worksheet.addRow({
        sno: index + 1,
        id: formatClientId(+row.id),
        state: row.state,
        legalBusinessName: row.legalBusinessName || '',
        doingBusinessAs: row.doingBusinessAs || '',
        contactPerson: row.contactPerson || '',
        contactEmail: row.contactEmail || '',
        contactNumber: row.contactNumber || '',
        status: row.annualTaxes?.length
          ? row.annualTaxes.some((tax) => !tax.approved)
            ? 'Added'
            : 'Approved'
          : 'Not-Added'
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, 'export.xlsx');
  };

  const sendAnnualTaxReminderEmail = async (row: any) => {
    try {
      setLoading(true);
      const year = new Date(`${selectedDate}`).getFullYear();
      const data = await sendAnnualTaxReminder({
        clientId: row.id.toString(),
        year: year
      });

      if (data.meta.status === 200) {
        NotificationManager.success('Annual tax reminder sent successfully');
        await getClients(new Date(`${selectedDate}`));
      }
    } catch (error) {
      NotificationManager.error('Failed to send annual tax reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAnnualTax = async (row: any) => {
    try {
      setLoading(true);
      const year = new Date(`${selectedDate}`).getFullYear();

      const data = await approveAnnualTax({
        clientId: row.id.toString(),
        year: year,
        approved: true,
        emailSent: true
      });

      if (data.meta.status === 200) {
        NotificationManager.success('Annual tax approved successfully');
        await getClients(new Date(`${selectedDate}`));
      }
    } catch (error) {
      NotificationManager.error('Failed to approve annual tax');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
  };

  const handleStateChange = (value: string) => {
    setSelectedState(value);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleFilters = (data: any) => {
    const filteredData = data.filter((item: any) => {
      let isMatch = true;

      if (
        selectedState &&
        selectedState !== 'All' &&
        selectedState !== item.state
      ) {
        isMatch = false;
      }

      if (isMatch) {
        if (selectedStatus === 'Approved') {
          if (
            !item.annualTaxes?.length ||
            item.annualTaxes.some((tax) => !tax.approved)
          ) {
            isMatch = false;
          }
        } else if (selectedStatus === 'Not-Added') {
          if (item.annualTaxes?.length) {
            isMatch = false;
          }
        } else if (selectedStatus === 'Added') {
          if (
            !item.annualTaxes?.length ||
            !item.annualTaxes.some((tax) => !tax.approved)
          ) {
            isMatch = false;
          }
        } else if (
          selectedStatus &&
          selectedStatus !== 'All' &&
          selectedStatus !== 'Approved' &&
          selectedStatus !== 'Not-Added' &&
          selectedStatus !== 'Added'
        ) {
          isMatch = false;
        }
      }

      if (
        isMatch &&
        searchValue &&
        item.legalBusinessName &&
        !item.legalBusinessName
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      ) {
        isMatch = false;
      }

      return isMatch;
    });

    setClients(filteredData);
  };

  const handleClear = () => {
    setSelectedStatus('All');
    setSelectedState('All');
    setSearchValue('');
  };

  useEffect(() => {
    handleFilters(unSortedClients);
  }, [
    selectedState,
    selectedStatus,
    searchValue,
    selectedDate,
    unSortedClients
  ]);

  useEffect(() => {
    const date = moment();
    getClients(date.toDate());
    handleClear();
  }, []);

  return (
    <Box mx={5}>
      {loading && (
        <div
          className="loader-wrapper"
          style={{ textAlign: 'center', marginTop: '10px', marginBottom: '10px' }}
        >
          <BarLoader color={'#1976D2'} loading={loading} width={'100%'} />
        </div>
      )}
      <Typography
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          color: '#1976D2',
          fontSize: '1.5em',
          my: 2
        }}
      >
        Annual Report
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}>
          <InputLabel htmlFor="businessFormationDate">Year</InputLabel>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ width: '100%' }}>
              <DatePicker
                views={['year']}
                openTo="year"
                value={selectedDate}
                onChange={handleYearChange}
                renderInput={(props) => (
                  <TextField {...props} style={{ width: '100%' }} />
                )}
                InputProps={{
                  size: 'small',
                  name: 'businessFormationDate'
                }}
              />
            </Box>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6} md={2}>
          <InputLabel htmlFor="state">State</InputLabel>
          <Autocomplete
            id="state"
            options={['All', ...states]}
            value={selectedState}
            onChange={(_, newValue) => handleStateChange(newValue)}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" size="small" />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3.5}>
          <InputLabel htmlFor="client-search">Client Name</InputLabel>
          <TextField
            sx={{ width: '100%' }}
            id="client-search"
            type="search"
            size="small"
            variant="outlined"
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={6} md={1.5}>
          <InputLabel htmlFor="client">Status</InputLabel>
          <Autocomplete
            id="client"
            options={['All', 'Added', 'Approved', 'Not-Added']}
            value={selectedStatus}
            onChange={(_, newValue) => handleStatusChange(newValue)}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" size="small" />
            )}
          />
        </Grid>
        <Grid item xs={3} md={1.5} sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button
            variant="contained"
            color="warning"
            onClick={handleClear}
            fullWidth
          >
            Clear
          </Button>
        </Grid>
        <Grid item xs={3} md={1.5} sx={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button
            variant="contained"
            color="success"
            onClick={handleExportToExcel}
            fullWidth
            startIcon={<FileDownloadIcon />}
          >
            Export
          </Button>
        </Grid>
      </Grid>

      <Box mt={3}>
        <Paper sx={{ width: '100%', overflow: 'hidden' }} elevation={3}>
            <TableContainer sx={{ width: '100%', overflow: 'auto' }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align as any}
                        style={{
                          minWidth: column.minWidth,
                          fontWeight: '800',
                          color: '#1976D2',
                          backgroundColor: '#f5f5f5'
                        }}
                        sx={styles.cell}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedSalesTax()
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                        <TableCell sx={styles.cell}>{index + 1}</TableCell>
                        <TableCell sx={styles.cell}>
                          {formatClientId(+row.id) || 'N/A'}
                        </TableCell>
                        <TableCell sx={styles.cell}>{row.state}</TableCell>
                        <TableCell sx={styles.cell}>
                          {row.legalBusinessName}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.doingBusinessAs}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.contactPerson}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.contactEmail}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.contactNumber}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          <Box
                            component="div"
                            sx={{
                              color: row.annualTaxes?.length
                                ? row.annualTaxes.some((tax) => !tax.approved)
                                  ? '#FFB302'
                                  : 'green'
                                : '#FF3838'
                            }}
                          >
                            {row.annualTaxes?.length
                              ? row.annualTaxes.some((tax) => !tax.approved)
                                ? 'Added'
                                : 'Approved'
                              : 'Not-Added'}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            ...styles.cell,
                            display: 'flex',
                            justifyContent: 'center'
                          }}
                        >
                          {!row.annualTaxes?.some((tax) => tax.emailSent) &&
                            !row.annualTaxes?.some((tax) => tax.approved) && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                sx={{ height: '25px' }}
                                onClick={() => handleApproveAnnualTax(row)}
                              >
                                Approve
                              </Button>
                            )}
                          {row.annualTaxes?.some((tax) => tax.emailSent) &&
                            row.annualTaxes?.some((tax) => tax.approved) && (
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ height: '25px' }}
                                onClick={() => {
                                  sendAnnualTaxReminderEmail(row);
                                }}
                              >
                                Remind
                              </Button>
                            )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={clients.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Box>
    </Box>
  );
};

export default AnnualReport;
